/**
 * ExecutorPostDeathDashboard — shown when deathEvent.status === "verified".
 * s127 fix: the full estate unlocks on verification ALONE, regardless of sharing_tier
 * (which gates the pre-death view only — see lib/executor-access.ts).
 *
 * Shows:
 *  - Status bar (deceased name, date of death, settlement day counter)
 *  - Executor Priority Banner (device + email; Cat A values show a secure-file pointer)
 *  - Estate Administration Checklist card
 *  - 4 quick-access tool cards
 *  - Collapsible full estate view link
 */

import { useQuery } from "@tanstack/react-query";
import { useDeathEvent } from "@/lib/use-death-event";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Calendar, ChevronRight, ClipboardList, DollarSign, Download, ExternalLink, FileText, Files, Lock, Mail, MapPin, Phone, Receipt, ShieldAlert } from "lucide-react";
import { formatDateOnlyLong } from "@shared/date-format";
import { HOME_BOARD, RULED_EXECUTOR_WORDS, fill } from "@/components/plan/planBoard";

interface ExecutorPostDeathDashboardProps {
  clientId: string;
}

export default function ExecutorPostDeathDashboard({ clientId }: ExecutorPostDeathDashboardProps) {
  const [, setLocation] = useLocation();

  // Get death event for status bar
  const { data: deathEvent } = useDeathEvent(clientId);

  // Get planner name from contexts
  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
  });
  const myContext = contexts?.executorFor?.find((c: any) => c.clientId === clientId);
  const plannerName = myContext?.plannerName ?? "the plan owner";
  // 🔴 s200 — the fallback is deliberate and correct when printed WHOLE ("the plan owner's estate").
  // Chopping it to its first word and appending a possessive rendered "the's". Derive the first
  // name from the REAL value only, never the fallback, and give every caller a null branch —
  // the shape GenericCategoryPage already uses for plannerFirstName.
  const plannerFirst = myContext?.plannerName?.trim().split(/\s+/)[0] || null;

  // s126 Rider 3 (DASH-NEXT-1): the "What to do next" card reads the CANONICAL task summary
  // (done / remaining / critical), not the legacy 69-item phase checklist. Full legacy
  // retirement is CHECKLIST-RETIRE-1 (Phase-0-gated) — this changes only what the card reads.
  const { data: taskListData } = useQuery<{
    summary: { totalTasks: number; doneTasks: number; criticalRemaining: number };
  }>({
    queryKey: [`/api/executor/${clientId}/tasks`],
    enabled: !!clientId,
  });
  const taskSummary = taskListData?.summary;

  const { data: riskProfile } = useQuery<{
    totalScore: number;
    riskLevel: "low" | "moderate" | "significant";
    completedAt: string | null;
  } | null>({
    queryKey: [`/api/clients/${clientId}/risk-profile`],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/risk-profile`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: Infinity,
  });

  // Get executor priorities (device + primary email; Cat A values arrive as pointers, never values)
  const { data: priorities } = useQuery<any>({
    queryKey: ["/api/clients", clientId, "executor-priorities"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/executor-priorities`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!clientId,
  });

  // Settlement day counter
  const dateOfDeath = deathEvent?.dateOfDeath
    ? new Date(deathEvent.dateOfDeath)
    : deathEvent?.createdAt
    ? new Date(deathEvent.createdAt)
    : null;

  const settlementDay = dateOfDeath
    ? Math.max(1, Math.floor((Date.now() - dateOfDeath.getTime()) / (1000 * 60 * 60 * 24)) + 1)
    : null;

  const toolCards = [
    {
      icon: Files,
      title: "Estate Documents",
      description: "Government forms, pre-filled from the estate plan. Download, review, file.",
      link: `/executor/${clientId}/estate-documents`,
      available: true,
    },
    {
      // s237 (D-237.75, Reade): the tile opens Where things are — the same page as the sidebar item (D-237.63 took the
      // classic Where Is Everything out of the sidebar; the classic page still opens from its address).
      icon: MapPin,
      title: HOME_BOARD.labels.where,
      description: fill(RULED_EXECUTOR_WORDS.whereTile, { planner: plannerFirst ?? plannerName }),
      link: `/executor/${clientId}/where`,
      available: true,
    },
    {
      icon: FileText,
      title: "Letter Generator",
      description: "Pre-filled notification letters to institutions",
      link: `/executor/${clientId}/letters`,
      available: true,
    },
    {
      icon: Receipt,
      title: "Statement Scanner",
      description: "Upload bank and credit card statements to discover recurring obligations",
      link: `/executor/${clientId}/statement-scanner`,
      available: true,
    },
    {
      icon: DollarSign,
      title: "Estate Financial Summary",
      description: "Balance sheet, monthly obligations, and probate tax estimate",
      link: `/executor/${clientId}/estate-summary`,
      available: true,
    },
  ];

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      {/* Status bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <p className="text-base text-muted-foreground">You're settling {plannerName}'s estate.</p>
          <h1 className="text-2xl font-bold">{plannerName}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
            {dateOfDeath && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Passed away {formatDateOnlyLong(deathEvent?.dateOfDeath)}
              </span>
            )}
            {settlementDay && (
              <Badge variant="outline" className="text-xs">
                Day {settlementDay} of estate settlement
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Intro copy */}
      <p className="text-sm text-muted-foreground -mt-2">
        Here's what's in place, and what's next. Take your time.
      </p>

      {/* Priority Banner.
          EX-6 Stage 2 (s200): this used to promise "phone and email" access and hand over neither.
          The device password and the email password are Category A — they never reach the server —
          so the two cards rendered blank while the heading promised access. They now carry the same
          labelled secure-file pointer every other honest executor surface uses, and the heading
          says where things are rather than promising to open them. */}
      {priorities?.hasData && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-amber-900 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {/* 🔴 s200 — "Start with X's phone and email" was wrong whenever the banner holds ONLY the
                  secure-file location, which the f332cfab fix created by adding that third row: Test
                  Client rendered that heading above a lone "Kept here: Home PC" with no phone and no
                  email. This wording is true in every combination measured, and mirrors the planner-side
                  section name ("Storing Sensitive Info") from the reader's side — the planner stores,
                  the reader finds. `plannerFirst` is the guarded value from 6e386db2; a raw
                  .split(" ")[0] here renders "the's sensitive info" while the name is loading. */}
              {plannerFirst ? `${plannerFirst}'s sensitive info — and how to get to it` : "Sensitive info — and how to get to it"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(priorities.phoneDevice || priorities.phonePin) && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-card border border-amber-200 px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-amber-700 shrink-0" />
                  <div>
                    <span className="font-medium text-amber-900">{priorities.phoneDevice ?? "Phone"}</span>
                    {priorities.phonePin?.secureFilePointer && (
                      <span className="text-amber-700 ml-2 inline-flex items-center gap-1">
                        {priorities.phonePin.label}: <Lock className="h-3 w-3" /> in the secure file
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(priorities.emailUsername || priorities.emailAccount || priorities.emailPassword) && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-card border border-amber-200 px-3 py-2">
                <div className="flex items-start gap-2 text-sm">
                  <Mail className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div className="space-y-0.5">
                    <div className="font-medium text-amber-900">{priorities.emailAccount ?? "Primary Email"}</div>
                    {priorities.emailUsername && (
                      <div className="text-amber-700">
                        Email: <span className="font-mono font-medium text-amber-900">{priorities.emailUsername}</span>
                      </div>
                    )}
                    {priorities.emailPassword?.secureFilePointer && (
                      <div className="text-amber-700 inline-flex items-center gap-1">
                        {priorities.emailPassword.label}: <Lock className="h-3 w-3" /> in the secure file
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* 🔴 s200 BANNER-REPOINT — where the secure file is kept.
                Essential #1 has always collected this and the banner never showed it. Telling an
                executor a credential is "in the secure file" without telling them WHERE the file is
                was half an answer; this is the line that lets them actually act. */}
            {priorities.secureFileLocation && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-card border border-amber-200 px-3 py-2">
                <div className="flex items-start gap-2 text-sm">
                  <Lock className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-amber-900">Secure Information File</div>
                    <div className="text-amber-700">
                      Kept here: <span className="font-medium text-amber-900">{priorities.secureFileLocation}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* What to do next — canonical task summary (s126 Rider 3, DASH-NEXT-1) */}
      <Card className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setLocation(`/executor/${clientId}/tasks`)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <ClipboardList className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">What to do next</p>
                <p className="text-sm text-muted-foreground">
                  {taskSummary
                    ? `${taskSummary.doneTasks} of ${taskSummary.totalTasks} tasks done.`
                    : "Every step of closing the estate"}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </div>
          {taskSummary && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                <span className="font-semibold text-foreground">{taskSummary.doneTasks}</span> done
              </span>
              <span className="text-muted-foreground">
                <span className="font-semibold text-foreground">{Math.max(0, taskSummary.totalTasks - taskSummary.doneTasks)}</span> remaining
              </span>
              {taskSummary.criticalRemaining > 0 && (
                <span className="text-destructive font-semibold">{taskSummary.criticalRemaining} critical</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estate Risk Profile card */}
      <Card className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setLocation(`/executor/${clientId}/risk-profile`)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <ShieldAlert className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Estate Risk Profile</p>
                {riskProfile ? (
                  <p className="text-sm text-muted-foreground">
                    Risk score: {riskProfile.totalScore} of 14 —{" "}
                    <span className={
                      riskProfile.riskLevel === "low" ? "text-green-700" :
                      riskProfile.riskLevel === "moderate" ? "text-orange-700" : "text-red-700"
                    }>
                      {riskProfile.riskLevel === "low" ? "low risk" :
                       riskProfile.riskLevel === "moderate" ? "moderate risk" : "significant risk"}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not yet completed</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!riskProfile && (
                <span className="text-xs font-medium text-primary border border-primary/30 rounded px-2 py-0.5">
                  Assess Risk <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              )}
              {riskProfile && (
                <span className="text-xs text-muted-foreground border rounded px-2 py-0.5">
                  Review <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              )}
            </div>
          </div>
          {riskProfile?.riskLevel === "significant" && (
            <p className="text-xs text-red-700 mt-2 ml-11">
              Consider talking to an estate lawyer about executor insurance (E&O coverage).
            </p>
          )}
        </CardContent>
      </Card>



      {/* Tool cards */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Start here</h2>
        <div className="grid grid-cols-2 gap-3">
          {toolCards.map((tool) => {
            const Icon = tool.icon;
            return (
              <Card
                key={tool.title}
                className={`transition-shadow ${tool.available ? "cursor-pointer hover:shadow-md" : "opacity-60"}`}
                onClick={() => tool.available && tool.link && setLocation(tool.link)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`rounded-md p-2 shrink-0 ${tool.available ? "bg-primary/10" : "bg-muted"}`}>
                      <Icon className={`w-5 h-5 ${tool.available ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{tool.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{tool.description}</p>
                      {!tool.available && (
                        <Badge variant="outline" className="text-xs mt-1">Coming soon</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* View full estate */}
      <Card className="border-dashed">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Full estate plan</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {plannerFirst ? `Browse all sections of ${plannerFirst}'s estate plan (read-only)` : "Browse all sections of the estate plan (read-only)"}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLocation(`/executor/${clientId}/estate`)}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open estate plan
            </Button>
          </div>
          {/* The two-document audit pair (EXECUTOR-WRITE-1, D-124): the immutable handoff snapshot
              (the plan as the planner left it) + the executor's record of changes made since. Both
              are same-origin GETs with the executor session cookie; each streams an attachment PDF. */}
          <div className="flex flex-col items-start gap-1.5">
            <a
              href={`/api/clients/${clientId}/estate-snapshot/download`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              data-testid="download-estate-snapshot"
            >
              <Download className="w-3.5 h-3.5" />
              Download the plan as {plannerName} left it
            </a>
            <a
              href={`/api/clients/${clientId}/executor-change-log/download`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              data-testid="download-executor-change-log"
            >
              <Download className="w-3.5 h-3.5" />
              Download your record of changes
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
